import { useRouter } from "next/router";
import { useState } from "react";
import { useEffect } from "react";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import { withScope, captureMessage } from "@sentry/nextjs";
import { formatReadableToShippingID } from "@/utils/identifiers";

import DashboardLayout from "../../components/dashboard/layout";

const StartHere = dynamic(() =>
  import("../../components/dashboard/start-here")
);
const GetAQuote = dynamic(() =>
  import("../../components/dashboard/get-a-quote")
);
const CanIShip = dynamic(() => import("../../components/dashboard/can-i-ship"));
const Orders = dynamic(() => import("../../components/dashboard/orders"));

const ShipmentDetail = dynamic(() =>
  import("../../components/dashboard/shipment-detail/shipment-detail")
);
const Profile = dynamic(() =>
  import("../../components/dashboard/account/profile")
);
const SavedBoxes = dynamic(() =>
  import("../../components/dashboard/account/saved-boxes")
);
const SavedRecipients = dynamic(() =>
  import("../../components/dashboard/account/saved-recipients")
);
const Cart = dynamic(() => import("../../components/dashboard/cart"));
const Resources = dynamic(() => import("../../components/dashboard/resources"));
const Billing = dynamic(() =>
  import("../../components/dashboard/account/billing")
);
const AdvancedShipment = dynamic(() =>
  import("@/features/ecommerce/advanced-shipment")
);
const AdvancedShipmentImportFromFile = dynamic(() =>
  import(
    "@/features/ecommerce/advanced-shipment/add-shipment-features/import-from-file-page"
  )
);

const ShopifyInstall = dynamic(() =>
  import("@/features/ecommerce/shopify/install")
);

const ShopifySettings = dynamic(() =>
  import("@/features/ecommerce/shopify/settings")
);

const CustomsDescriptionSetting = dynamic(() =>
  import("@/features/ecommerce/shopify/settings/customs-description")
);

export default function Dashboard() {
  const router = useRouter();
  const [mainComponent, setMainComponent] = useState(null);
  const [showMainComponent, setShowMainComponent] = useState(false);
  const { data: session, status, update } = useSession();

  useEffect(() => {
    // When session is not loading and there's no session, redirect to register
    if (status !== "loading" && !session) {
      router.push("/register");
    }
  }, [session, status, router]);

  useEffect(() => {
    update();
  }, []);

  useEffect(() => {
    if (session) {
      if (!session.user_segment) {
        // TODO: would this work for users who just made an order? would session be updated?
        fetch("/api/user/segment")
          .then((res) => res.json())
          .then((data) => {
            if (!data) {
              router.push("/dashboard/setup");
            } else {
              setShowMainComponent(true);
            }
          });
      } else {
        setShowMainComponent(true);
      }
    }
  }, [session]);

  useEffect(() => {
    // Wait until the router has finished populating the query parameters
    if (!router.isReady) return;

    const pathWithoutQuery = router.asPath.split("?")[0].replace(/#$/, "");
    const dynamicShipmentDetailPathRegex = /^\/dashboard\/orders\/.+/;

    if (dynamicShipmentDetailPathRegex.test(pathWithoutQuery)) {
      let shipmentID = pathWithoutQuery.split("/").pop();
      shipmentID = formatReadableToShippingID(shipmentID);
      const submissionID = router.query.token;
      setMainComponent(
        <ShipmentDetail shipmentID={shipmentID} submissionID={submissionID} />
      );
    } else {
      switch (pathWithoutQuery) {
        case "/dashboard/start-here":
          setMainComponent(<StartHere />);
          break;
        case "/dashboard/get-a-quote":
          setMainComponent(<GetAQuote />);
          break;
        case "/dashboard/orders":
          setMainComponent(<Orders />);
          break;
        case "/dashboard/account/profile":
          setMainComponent(<Profile />);
          break;
        case "/dashboard/account/saved-boxes":
          setMainComponent(<SavedBoxes />);
          break;
        case "/dashboard/account/saved-recipients":
          setMainComponent(<SavedRecipients />);
          break;
        case "/dashboard/account/customs":
          setMainComponent(<CustomsDescriptionSetting />);
          break;
        case "/dashboard/cart":
          setMainComponent(<Cart />);
          break;
        case "/dashboard/resources":
          setMainComponent(<Resources />);
          break;
        case "/dashboard/account/billing":
          setMainComponent(<Billing />);
          break;
        case "/dashboard/advanced-shipment":
          setMainComponent(<AdvancedShipment />);
          break;
        case "/dashboard/advanced-shipment/import":
          setMainComponent(<AdvancedShipmentImportFromFile />);
          break;
        case "/dashboard/ecommerce/shopify/install":
          setMainComponent(<ShopifyInstall />);
          break;
        case "/dashboard/ecommerce/shopify/settings":
          setMainComponent(<ShopifySettings />);
          break;
        case "/dashboard/can-i-ship":
          setMainComponent(<CanIShip />);
          break;
        default:
          withScope((scope) => {
            scope.setLevel("warning");
            scope.setExtra("router.asPath", router.asPath);
            scope.setExtra("router.query", router.query);
            captureMessage("404");
          });
          setMainComponent(<Error404 />);
      }
    }
  }, [router.asPath, router.isReady, router.query]);

  return (
    <>
      <DashboardLayout>
        {session && showMainComponent && <>{mainComponent}</>}
      </DashboardLayout>
    </>
  );
}

const Error404 = () => {
  return (
    <div className="flex flex-col items-center justify-center h-screen px-8 text-center">
      <h1 className="text-4xl font-bold text-pink">404</h1>
      <p className="font-light text-base">
        The page you are looking for does not exist.
      </p>
    </div>
  );
};
